import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as loginApi, impersonateAdmin as impersonateAdminApi, getMe } from '../lib/api';
import { clearMessagesCache } from '../lib/messagesCache';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalToken, setOriginalToken] = useState(null);

  // Simple initialization - run once on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const original = localStorage.getItem('original_token');
    
    if (token && savedUser) {
      try {
        // Hydrate immediately from localStorage so the UI doesn't flash
        setUser(JSON.parse(savedUser));
        
        if (original) {
          setIsImpersonating(true);
          setOriginalToken(original);
        }
        
        // Then refresh from server so dynamic fields (trial state, plan, etc.)
        // are always up to date — and the user is forced out if the backend
        // revoked their session (deactivated, trial expired by limit, etc.)
        getMe()
          .then((res) => {
            if (res?.data) {
              setUser(res.data);
              localStorage.setItem('user', JSON.stringify(res.data));
            }
          })
          .catch((err) => {
            // Backend rejected the token (401, 402 with revoked session, etc.)
            // — clear local state and force re-login.
            if (err?.response?.status === 401) {
              clearMessagesCache();
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              localStorage.removeItem('original_token');
              setUser(null);
              setIsImpersonating(false);
              setOriginalToken(null);
            }
          })
          .finally(() => setLoading(false));
        return; // loading will be cleared inside the .finally
      } catch (err) {
        console.error('Error parsing saved user:', err);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('original_token');
      }
    }
    
    setLoading(false);
  }, []); // Empty array - runs once on mount

  const login = async (email, password) => {
    clearMessagesCache(); // descarta cache de qualquer sessão anterior na mesma aba
    const response = await loginApi(email, password);
    const { token, user: userData } = response.data;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    clearMessagesCache();
    // Zera o "já levei este usuário ao QR nesta entrada": quem entrar depois (inclusive outra
    // conta na mesma aba) tem direito ao redirecionamento de novo.
    sessionStorage.removeItem('wa_onboarding_redirected');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    setUser(null);
    setIsImpersonating(false);
    setOriginalToken(null);
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    localStorage.setItem('user', JSON.stringify(updatedUserData));
  };

  /**
   * Re-fetches the current user from the backend.
   * Use after billing/plan changes so trial_blocked, plan info, etc. are fresh
   * without forcing a logout/login cycle.
   */
  const refreshUser = async () => {
    try {
      const res = await getMe();
      if (res?.data) {
        setUser(res.data);
        localStorage.setItem('user', JSON.stringify(res.data));
        return res.data;
      }
    } catch (err) {
      // 401 → user will be cleared by the boot effect on next mount
      console.warn('refreshUser failed:', err?.message || err);
    }
    return null;
  };

  const impersonate = async (adminId) => {
    try {
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      const response = await impersonateAdminApi(adminId);
      
      // Save original user AND token
      localStorage.setItem('original_token', currentToken);
      localStorage.setItem('original_user', currentUser); // Save super admin user
      
      // Save impersonated user
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Update state
      clearMessagesCache(); // não vazar histórico do super admin para o tenant impersonado
      setUser(response.data.user);
      setIsImpersonating(true);
      setOriginalToken(currentToken);

      return true;
    } catch (error) {
      console.error('Erro ao impersonar:', error);
      return false;
    }
  };

  const stopImpersonating = async () => {
    try {
      const originalToken = localStorage.getItem('original_token');
      const originalUser = localStorage.getItem('original_user');
      
      if (!originalToken || !originalUser) {
        throw new Error('Dados originais não encontrados');
      }
      
      // Restore original token and user
      localStorage.setItem('token', originalToken);
      localStorage.setItem('user', originalUser);
      localStorage.removeItem('original_token');
      localStorage.removeItem('original_user');
      
      // Update state
      clearMessagesCache(); // não manter histórico do tenant impersonado ao voltar
      setUser(JSON.parse(originalUser));
      setIsImpersonating(false);
      setOriginalToken(null);

      return true;
    } catch (error) {
      console.error('Erro ao voltar:', error);
      logout();
      return false;
    }
  };

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAdmin = user?.role === 'ADMIN';
  const isEditor = user?.role === 'USER_EDITOR';
  const isUserSend = user?.role === 'USER_SEND';
  const isUserRead = user?.role === 'USER_READ';
  const canSend = isSuperAdmin || isAdmin || isEditor || isUserSend;
  const canEdit = isSuperAdmin || isAdmin || isEditor;
  const canManageUsers = isSuperAdmin || isAdmin;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        updateUser,
        refreshUser,
        isSuperAdmin,
        isAdmin,
        isEditor,
        isUserSend,
        isUserRead,
        canSend,
        canEdit,
        canManageUsers,
        isImpersonating,
        impersonate,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
